// Lightweight client-side error capture.
//
// Wires window.onerror + window.onunhandledrejection into:
//   1) a circular in-memory + localStorage buffer (last 50 errors), so
//      a future "X is broken" report can be paired with a recent stack
//      trace just by running `JSON.parse(localStorage.snordeluxe_errors)`
//      in DevTools;
//   2) a best-effort beacon POST to /api/error — if the endpoint isn't
//      live yet it 404s silently and the local buffer still works. When
//      the backend adds the endpoint, this starts reporting automatically.
//
// Intentionally tiny + side-effect-free at import time (only installs
// listeners when install() is called from main).

const SERVER_URL = '';            // same convention as chat.js
const LS_KEY = 'snordeluxe_errors';
const MAX_KEEP = 50;
const RECENT_DEDUPE_MS = 5000;    // drop identical errors within 5s

let buffer = null;
let lastSignatureAt = new Map();

function loadBuffer() {
    if (buffer) return buffer;
    try {
        const raw = localStorage.getItem(LS_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        buffer = Array.isArray(parsed) ? parsed.slice(-MAX_KEEP) : [];
    } catch (_) {
        buffer = [];
    }
    return buffer;
}

function persistBuffer() {
    try { localStorage.setItem(LS_KEY, JSON.stringify(buffer)); } catch (_) {}
}

function signatureFor(entry) {
    // (message, top-frame) is enough to dedupe per-tick spam from a
    // repeating handler — keeps the buffer useful for diagnosis.
    return `${entry.message}|${(entry.stack || '').split('\n')[1] || ''}`;
}

function record(kind, info) {
    const now = Date.now();
    const entry = {
        kind,
        message: info.message || String(info.error || info.reason || 'unknown'),
        stack: (info.error && info.error.stack) || (info.reason && info.reason.stack) || null,
        source: info.filename || null,
        line: info.lineno || null,
        col: info.colno || null,
        href: typeof location !== 'undefined' ? location.href : null,
        ua: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        at: new Date(now).toISOString(),
    };
    const sig = signatureFor(entry);
    const lastAt = lastSignatureAt.get(sig) || 0;
    if (now - lastAt < RECENT_DEDUPE_MS) return; // de-spam
    lastSignatureAt.set(sig, now);

    loadBuffer();
    buffer.push(entry);
    if (buffer.length > MAX_KEEP) buffer.shift();
    persistBuffer();

    // Best-effort beacon. sendBeacon doesn't block + survives unload.
    // 404 here is fine — we keep the local copy for inspection.
    try {
        const payload = JSON.stringify(entry);
        if (navigator.sendBeacon) {
            navigator.sendBeacon(`${SERVER_URL}/api/error`, new Blob([payload], { type: 'application/json' }));
        } else {
            fetch(`${SERVER_URL}/api/error`, {
                method: 'POST',
                keepalive: true,
                headers: { 'Content-Type': 'application/json' },
                body: payload,
            }).catch(() => {});
        }
    } catch (_) {}
}

export function installTelemetry() {
    if (typeof window === 'undefined') return;
    if (window.__snordeluxeTelemetryInstalled) return;
    window.__snordeluxeTelemetryInstalled = true;

    window.addEventListener('error', (e) => {
        record('error', { message: e.message, error: e.error, filename: e.filename, lineno: e.lineno, colno: e.colno });
    });
    window.addEventListener('unhandledrejection', (e) => {
        record('unhandledrejection', { reason: e.reason });
    });

    // Localhost-only dev affordance: a friendly inspector for the buffer.
    try {
        const h = window.location && window.location.hostname;
        if (h === 'localhost' || h === '127.0.0.1') {
            window.__snordeluxeErrors = () => loadBuffer().slice();
            window.__snordeluxeClearErrors = () => { buffer = []; persistBuffer(); };
        }
    } catch (_) {}
}
